// gatsby-browser.js
import React from 'react';
import { showLoader, hideLoader, forceHideLoader } from './src/utils/loader';
import { AuthProvider } from './src/context/AuthContext';
import LanguageContextProvider from './src/context';
import "@popperjs/core/dist/umd/popper.min";
import "bootstrap/dist/css/bootstrap.min.css";
import './src/assets/css/index.scss';
import { Helmet } from 'react-helmet';

// Define private routes (legacy exact routes + real Escola routes).
const privateExactPaths = ["/login", "/private-school-page", "/private-lesson-page", "/escola-biblica-online"];
const privatePathPrefixes = [
  "/escola-biblica-online",
  "/es/escola-biblica-online",
  "/en/escola-biblica-online",
  "/escola/lesson",
  "/es/escola/lesson",
  "/en/escola/lesson",
];

const normalizePath = (path = "") => {
  const normalized = path.replace(/\/+$/, "");
  return normalized || "/";
};

const isPrivatePath = (path = "") => {
  const pathname = normalizePath(path);
  if (privateExactPaths.includes(pathname)) {
    return true;
  }

  return privatePathPrefixes.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
};

const normalizeRoutePathname = (pathname = "") => {
  const normalized = pathname.replace(/\/+$/, "");
  return normalized || "/";
};

const getCookieConsent = (name) => {
  if (typeof document === "undefined") return false;
  return document.cookie
    .split(";")
    .map((cookie) => cookie.trim())
    .some((cookie) => cookie === `${name}=true`);
};

const expireGoogleCookies = (names) => {
  if (typeof document === "undefined") return;

  const domains = ["", window.location.hostname, ".unida.es"];
  document.cookie.split(";").forEach((cookie) => {
    const name = cookie.split("=")[0].trim();
    if (!names.some((prefix) => name === prefix || name.startsWith(`${prefix}_`))) return;

    domains.forEach((domain) => {
      const domainPart = domain ? `; domain=${domain}` : "";
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domainPart}; SameSite=Lax`;
    });
  });
};

let previousAnalyticsConsent;

const syncGoogleConsent = () => {
  if (typeof window === "undefined") return;

  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() {
    window.dataLayer.push(arguments);
  };

  const analyticsGranted = getCookieConsent("gatsby-gdpr-google-analytics");
  const adsGranted = getCookieConsent("gatsby-gdpr-google-ads");

  window.gtag("consent", "update", {
    analytics_storage: analyticsGranted ? "granted" : "denied",
    ad_storage: adsGranted ? "granted" : "denied",
    ad_user_data: adsGranted ? "granted" : "denied",
    ad_personalization: adsGranted ? "granted" : "denied",
  });

  // The initial cookieless page_view may already have fired while the visitor
  // was reading the banner. Record the current page once when they opt in.
  if (previousAnalyticsConsent === false && analyticsGranted) {
    window.gtag("event", "page_view", {
      page_path: window.location.pathname + window.location.search + window.location.hash,
      page_title: document.title,
    });
  }

  previousAnalyticsConsent = analyticsGranted;

  if (!analyticsGranted) expireGoogleCookies(["_ga", "_gid", "_gat"]);
  if (!adsGranted) expireGoogleCookies(["_gcl", "_gac"]);
};

const syncMetaConsent = () => {
  if (typeof window === "undefined") return;

  const metaGranted = getCookieConsent("gatsby-gdpr-facebook-pixel");

  if (!metaGranted) {
    if (typeof window.fbq === "function") window.fbq("consent", "revoke");
    return;
  }

  if (typeof window.fbq === "function") {
    window.fbq("consent", "grant");
    return;
  }

  // Load Meta only after an explicit opt-in. The GDPR plugin will reuse these
  // flags and continue tracking PageView events on Gatsby route changes.
  const fbq = function fbq() {
    if (fbq.callMethod) fbq.callMethod.apply(fbq, arguments);
    else fbq.queue.push(arguments);
  };
  window.fbq = fbq;
  window._fbq = fbq;
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = "2.0";
  fbq.queue = [];

  const script = document.createElement("script");
  script.async = true;
  script.src = "https://connect.facebook.net/en_US/fbevents.js";
  document.head.appendChild(script);

  window.gatsbyPluginGDPRCookiesFacebookPixelAdded = true;
  window.fbq("init", "191215913048943");
  window.gatsbyPluginGDPRCookiesFacebookPixelInitialized = true;
  window.fbq("track", "PageView");
};

const syncTrackingConsent = () => {
  syncGoogleConsent();
  syncMetaConsent();
};

// Force-disable old Gatsby offline service workers during migration.
export const onClientEntry = async () => {
  if (typeof window === "undefined") return;

  syncTrackingConsent();
  document.addEventListener("unida:cookie-consent-changed", syncTrackingConsent);
  window.addEventListener("focus", syncTrackingConsent);

  window.addEventListener("load", forceHideLoader);
  window.addEventListener("pageshow", forceHideLoader);

  if (!("serviceWorker" in navigator)) return;

  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.all(regs.map((reg) => reg.unregister()));
  } catch (error) {
    // no-op
  }

  if ("caches" in window) {
    try {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
    } catch (error) {
      // no-op
    }
  }
};

const isHashOnlyRouteUpdate = ({ location, prevLocation }) => (
  Boolean(
    location
    && prevLocation
    && normalizeRoutePathname(location.pathname) === normalizeRoutePathname(prevLocation.pathname)
    && location.search === prevLocation.search
    && location.hash !== prevLocation.hash
  )
);

// Handle route changes to show and hide the loader
export const onPreRouteUpdate = ({ location, prevLocation }) => {
  if (isHashOnlyRouteUpdate({ location, prevLocation })) {
    return;
  }

  showLoader();
};

export const onInitialClientRender = () => {
  forceHideLoader();
};

export const onRouteUpdate = ({ location, prevLocation }) => {
  if (isHashOnlyRouteUpdate({ location, prevLocation })) {
    forceHideLoader();
    return;
  }

  hideLoader();
};

// Conditionally import Bootstrap JS only in the browser
if (typeof window !== "undefined") {
  import("bootstrap/dist/js/bootstrap.min");
}

// Unified wrapRootElement function for both gatsby-browser.js and gatsby-ssr.js
export const wrapRootElement = ({ element }) => (
  <LanguageContextProvider langcode="ca">
    <AuthProvider>
      {element}
    </AuthProvider>
  </LanguageContextProvider>
);
// Add the wrapPageElement function for meta tags
export const wrapPageElement = ({ element, props }) => {
  const pageIsPrivatePath = isPrivatePath(props.location.pathname);

  return (
    <>
      {pageIsPrivatePath && (
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
      )}
      {element}
    </>
  );
};
